import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleCallback } from '@goodsteward/gs-auth-spa';
import { useAuthStore } from '@/stores/auth';
import { fetchMe } from '@/api/auth';
import { getCognitoConfig } from '@/auth/config';
import { AuthShell } from './AuthShell';

// /auth/callback — IdP redirect target after a successful login.
//
// Flow: read ?code+?state, exchange the code for tokens, write the access
// token into the auth store, then call /api/me to populate the rich
// {user, practice} payload (permissions, roleName, etc.) the rest of the
// app expects. Finally route the user to wherever they came from.
//
// /api/me has to run BEFORE the navigate — components on the destination
// route read user.permissions immediately, and a missing user would either
// flash 403s or redirect through the auth gate again.
//
// Errors funnel through /auth/error rather than rendering inline so
// every sign-in failure across the app lands on the same dedicated
// surface.

export function AuthCallbackPage() {
    const setCognitoSession = useAuthStore((s) => s.setCognitoSession);
    const setCognitoTokens  = useAuthStore((s) => s.setCognitoTokens);
    const logout            = useAuthStore((s) => s.logout);
    const nav = useNavigate();
    // StrictMode mounts each component twice in dev; the auth code is
    // single-use — the IdP rejects the second exchange — so we guard.
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;
        (async () => {
            try {
                const result = await handleCallback(getCognitoConfig());
                // Stash tokens FIRST so the /api/me fetch picks up the
                // Authorization header via the Axios interceptor.
                setCognitoTokens({
                    accessToken:  result.accessToken,
                    refreshToken: result.refreshToken,
                    expiresAt:    result.expiresAt,
                });
                const me = await fetchMe();
                setCognitoSession(
                    {
                        accessToken:  result.accessToken,
                        refreshToken: result.refreshToken,
                        expiresAt:    result.expiresAt,
                    },
                    me.user,
                    me.practice,
                );
                const dest = me.user.roleType === 'parent' ? '/portal' : (result.returnTo || '/');
                if (/^https?:\/\//i.test(dest)) {
                    window.location.assign(dest);
                } else {
                    nav(dest, { replace: true });
                }
            } catch (e) {
                // setCognitoTokens above persisted the access token before
                // /api/me ran. If /api/me threw we'd be left with a token
                // but no user — RequireAuth would let the next page load
                // through and the dashboard would render with user=null.
                // Clear the half-state before bouncing to /auth/error so
                // its "Try again" button lands in RequireAuth without a
                // token and re-runs the IdP redirect.
                logout();
                nav('/auth/error', {
                    replace: true,
                    state: {
                        message: e instanceof Error
                            ? `Could not complete sign-in: ${e.message}`
                            : 'Could not complete sign-in.',
                    },
                });
            }
        })();
    }, [setCognitoSession, setCognitoTokens, logout, nav]);

    return (
        <AuthShell>
            <p className="text-sm text-text-subtle">Signing you in…</p>
        </AuthShell>
    );
}
